/**
 * Component: SafetyValidationLogPage
 * SpecReferenceID: ADMIN-V1
 * Requirements: ADMIN-V1-R04
 *
 * Admin page for displaying safety validation outcomes.
 * ADMIN-V1-R04: System shall display safety validation outcomes with
 *               filtering, pagination, and export capabilities.
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  ShieldAlert,
  Loader2,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { subDays, format } from 'date-fns';

import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ExportButton, type ExportData } from './components/ExportButton';
import { DateRangePicker } from './components/DateRangePicker';

// ── Types ────────────────────────────────────────────────────────────────────

interface SafetyLogEntry {
  id: string;
  timestamp: string;
  user_id: string;
  session_id: string;
  correlation_id: string;
  query_text: string;
  decision: string;
  triggered_rules: string[];
  message: string | null;
  validation_time_ms: number | null;
  query_type: string;
  extra: Record<string, unknown>;
}

interface SafetyLogsResponse {
  logs: SafetyLogEntry[];
  total_count: number;
  offset: number;
  limit: number;
  has_more: boolean;
  allow_count: number;
  block_count: number;
  clarify_count: number;
}

type SortField = 'timestamp' | 'user_id' | 'decision' | 'query_text' | 'query_type';
type SortDirection = 'asc' | 'desc';

const PAGE_SIZES = [10, 25, 50] as const;
const DECISION_TYPES = ['all', 'allow', 'block', 'clarify'] as const;

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function decisionBadgeVariant(decision: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (decision) {
    case 'block':
      return 'destructive';
    case 'clarify':
      return 'secondary';
    case 'allow':
      return 'default';
    default:
      return 'outline';
  }
}

// ── Component ────────────────────────────────────────────────────────────────

export function SafetyValidationLogPage(): React.ReactElement {
  // Data state
  const [logs, setLogs] = useState<SafetyLogEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({ allow: 0, block: 0, clarify: 0 });

  // Filter state
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  const [decisionFilter, setDecisionFilter] = useState<string>('all');

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // Sort state
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Expanded rows
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // ── Fetch logs ──────────────────────────────────────────────────────────

  const fetchLogs = useCallback(async () => {
    if (!dateRange?.from || !dateRange?.to) return;

    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        start_time: dateRange.from.toISOString(),
        end_time: dateRange.to.toISOString(),
        offset: (page - 1) * pageSize,
        limit: pageSize,
      };
      if (decisionFilter !== 'all') {
        params['decision'] = decisionFilter;
      }

      const response = await api.get<SafetyLogsResponse>('/admin/logs/safety', { params });
      setLogs(response.data.logs);
      setTotalCount(response.data.total_count);
      setStats({
        allow: response.data.allow_count,
        block: response.data.block_count,
        clarify: response.data.clarify_count,
      });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [dateRange, page, pageSize, decisionFilter]);

  useEffect(() => {
    void fetchLogs();
  }, [fetchLogs]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [dateRange, decisionFilter, pageSize]);

  // ── Row expansion ──────────────────────────────────────────────────────

  const toggleRow = useCallback((id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // ── Sorting ──────────────────────────────────────────────────────────────

  const handleSort = useCallback((field: SortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDirection('asc');
      return field;
    });
  }, []);

  const sortedLogs = useMemo(() => {
    return [...logs].sort((a, b) => {
      const aVal = String(a[sortField] ?? '').toLowerCase();
      const bVal = String(b[sortField] ?? '').toLowerCase();
      const cmp = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [logs, sortField, sortDirection]);

  // ── Sort header button ───────────────────────────────────────────────────

  const SortButton = useCallback(
    ({ field, label }: { field: SortField; label: string }) => {
      const isActiveSort = sortField === field;
      const Icon = isActiveSort
        ? sortDirection === 'asc'
          ? ArrowUp
          : ArrowDown
        : ArrowUpDown;

      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8"
          onClick={() => handleSort(field)}
          aria-label={`Sort by ${label}${isActiveSort ? `, currently ${sortDirection === 'asc' ? 'ascending' : 'descending'}` : ''}`}
        >
          {label}
          <Icon className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      );
    },
    [sortField, sortDirection, handleSort],
  );

  // ── Export data ──────────────────────────────────────────────────────────

  const exportData: ExportData = useMemo(() => {
    const dateStr = dateRange?.from && dateRange?.to
      ? `${format(dateRange.from, 'yyyy-MM-dd')}_to_${format(dateRange.to, 'yyyy-MM-dd')}`
      : 'all-time';

    const rows = logs.map((log) => ({
      Timestamp: log.timestamp,
      User: log.user_id,
      Session: log.session_id,
      Decision: log.decision,
      'Triggered Rules': log.triggered_rules.join(', '),
      Query: log.query_text,
      Message: log.message ?? '',
      'Query Type': log.query_type,
    }));

    return {
      filename: `safety-validation-logs_${dateStr}`,
      headers: ['Timestamp', 'User', 'Session', 'Decision', 'Triggered Rules', 'Query', 'Message', 'Query Type'],
      rows,
    };
  }, [dateRange, logs]);

  // ── Pagination handler ──────────────────────────────────────────────────

  const handlePageSizeChange = useCallback((value: string) => {
    setPageSize(Number(value));
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ShieldAlert className="h-6 w-6" aria-hidden="true" />
            Safety Validation Logs
          </h1>
          <p className="text-muted-foreground mt-1">
            Safety rule validation outcomes for blocked, clarified, and allowed queries.
          </p>
        </div>
        <ExportButton data={exportData} disabled={loading} />
      </div>

      {/* Error alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Decision stats */}
      {!loading && !error && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.allow.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">Allowed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.block.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">Blocked</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.clarify.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">Clarified</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="flex-1">
              <DateRangePicker
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                disabled={loading}
              />
            </div>
            <div className="flex gap-2">
              <Select value={decisionFilter} onValueChange={setDecisionFilter}>
                <SelectTrigger className="w-[140px]" aria-label="Filter by decision">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DECISION_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type === 'all' ? 'All Decisions' : type.charAt(0).toUpperCase() + type.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs table */}
      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-lg">
            Log Entries
            {!loading && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({totalCount.toLocaleString()} total)
              </span>
            )}
          </CardTitle>
          <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="w-[100px]" aria-label="Rows per page">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size} rows
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12" aria-label="Loading logs">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
              <span className="sr-only">Loading safety validation logs...</span>
            </div>
          ) : sortedLogs.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">
              No log entries found for the selected filters.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]" />
                      <TableHead>
                        <SortButton field="timestamp" label="Timestamp" />
                      </TableHead>
                      <TableHead>
                        <SortButton field="user_id" label="User" />
                      </TableHead>
                      <TableHead>Triggered Rules</TableHead>
                      <TableHead>
                        <SortButton field="decision" label="Decision" />
                      </TableHead>
                      <TableHead>
                        <SortButton field="query_text" label="Query" />
                      </TableHead>
                      <TableHead>Message</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedLogs.map((log) => (
                      <React.Fragment key={log.id}>
                        <TableRow
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleRow(log.id)}
                          aria-expanded={expandedRows.has(log.id)}
                        >
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              aria-label={expandedRows.has(log.id) ? 'Collapse row' : 'Expand row'}
                              tabIndex={-1}
                            >
                              {expandedRows.has(log.id) ? (
                                <ChevronUp className="h-4 w-4" aria-hidden="true" />
                              ) : (
                                <ChevronDown className="h-4 w-4" aria-hidden="true" />
                              )}
                            </Button>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                            {formatDateTime(log.timestamp)}
                          </TableCell>
                          <TableCell className="font-medium max-w-[120px] truncate" title={log.user_id}>
                            {log.user_id}
                          </TableCell>
                          <TableCell className="text-sm max-w-[150px] truncate" title={log.triggered_rules.join(', ')}>
                            {log.triggered_rules.length > 0 ? log.triggered_rules.join(', ') : '—'}
                          </TableCell>
                          <TableCell>
                            <Badge variant={decisionBadgeVariant(log.decision)}>
                              {log.decision}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate" title={log.query_text}>
                            {log.query_text}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate" title={log.message ?? ''}>
                            {log.message ?? '—'}
                          </TableCell>
                        </TableRow>
                        {expandedRows.has(log.id) && (
                          <TableRow>
                            <TableCell colSpan={7} className="bg-muted/30 p-4">
                              <div className="space-y-3 text-sm">
                                <div>
                                  <span className="font-medium">Full Query:</span>
                                  <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{log.query_text}</p>
                                </div>
                                {log.triggered_rules.length > 0 && (
                                  <div>
                                    <span className="font-medium">Matched Rules:</span>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {log.triggered_rules.map((rule) => (
                                        <Badge key={rule} variant="outline">{rule}</Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {log.message && (
                                  <div>
                                    <span className="font-medium">Safety Message:</span>
                                    <p className="mt-1 text-muted-foreground">{log.message}</p>
                                  </div>
                                )}
                                <div className="flex gap-6 text-muted-foreground">
                                  <span>Session: {log.session_id}</span>
                                  <span>Type: {log.query_type}</span>
                                  {log.validation_time_ms != null && (
                                    <span>Validation: {log.validation_time_ms.toFixed(1)}ms</span>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount.toLocaleString()} entries
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default SafetyValidationLogPage;
